import { create } from 'zustand';
import { VirtualIdolContext, LiveChatMessage } from '../../../worker/app/chatbot/VirtualIdolPromptManager';
import { useAppStore } from '../use-app-store';

interface VirtualIdolContextStore {
  // Chat messages state
  liveChatMessages: LiveChatMessage[];
  currentTimestamp: number;
  
  // Stream state
  viewerCount: number;
  currentTopic: string;
  mood: 'excited' | 'chill' | 'chaotic' | 'focused';
  
  // Locale
  locale: 'EN' | 'JA' | 'ZH';
  
  // Actions
  setLiveChatMessages: (messages: LiveChatMessage[]) => void;
  addChatMessage: (message: LiveChatMessage) => void;
  setViewerCount: (count: number) => void;
  setCurrentTopic: (topic: string) => void;
  setMood: (mood: 'excited' | 'chill' | 'chaotic' | 'focused') => void;
  setLocale: (locale: 'EN' | 'JA' | 'ZH') => void;
  updateTimestamp: () => void;
  
  // Initialize system event subscription
  initializeSystemEventSubscription: () => void;
  
  // Get context data for AI
  getContextData: () => VirtualIdolContext;
}

export const useVirtualIdolContextStore = create<VirtualIdolContextStore>((set, get) => ({
  // Initial state
  liveChatMessages: [],
  currentTimestamp: Date.now(),
  viewerCount: 0,
  currentTopic: '',
  mood: 'chill',
  locale: 'EN',
  
  // Actions
  setLiveChatMessages: (messages) => set({ 
    liveChatMessages: messages.slice(-20), // Keep only last 20 messages
    currentTimestamp: Date.now() 
  }),
  
  addChatMessage: (message) => set((state) => {
    // Check if message already exists (same timestamp, username, and message)
    const isDuplicate = state.liveChatMessages.some(
      msg => msg.username === message.username && 
             msg.message === message.message
    );
    
    // Only add if not a duplicate
    if (!isDuplicate) {
      return {
        liveChatMessages: [...state.liveChatMessages, message].slice(-20),
        currentTimestamp: Date.now()
      };
    }
    
    // Return unchanged state if duplicate
    return state;
  }),
  
  setViewerCount: (count) => set({ viewerCount: count }),
  
  setCurrentTopic: (topic) => set({ currentTopic: topic }),
  
  setMood: (mood) => set({ mood }),
  
  setLocale: (locale) => set({ locale }),
  
  updateTimestamp: () => set({ currentTimestamp: Date.now() }),
  
  // Initialize system event subscription
  initializeSystemEventSubscription: () => {
    const { sub } = useAppStore.getState();
    
    // Subscribe to system_event messages for livechat
    const handleSystemEvent = (payload: any) => {
      if (payload?.role === 'livechat' && payload?.content) {
        const message: LiveChatMessage = {
          username: payload.content.username,
          message: payload.content.message,
          timestamp: payload.content.timestamp
        };
        get().addChatMessage(message);
      }
    };
    
    sub('system_event', handleSystemEvent);
  },
  
  // Get formatted context data for the AI
  getContextData: () => {
    const state = get();
    return {
      locale: state.locale,
      mode: 'assistant' as const,
      liveChatMessages: state.liveChatMessages,
      currentTimestamp: state.currentTimestamp,
      streamState: {
        viewerCount: state.viewerCount,
        currentTopic: state.currentTopic,
        mood: state.mood
      }
    };
  }
}));

// Hook to simulate fetching live chat messages (will be replaced with actual pump.fun integration)
export const useLiveChatSimulator = () => {
  const { addChatMessage, setViewerCount, updateTimestamp } = useVirtualIdolContextStore();
  
  // This will eventually be replaced with actual pump.fun chat integration
  const simulateIncomingMessage = () => {
    const sampleMessages = [
      // Crypto/Trading
      { username: "CryptoWeeb", message: "Lucy what do you think about the new anime season?" },
      { username: "DegenerateDan", message: "PUMP IT" },
      { username: "MoonBoi", message: "To the moon! 🚀" },
      { username: "DiamondHands420", message: "HODL the line!" },
      { username: "PaperHandsPete", message: "Should I sell? I'm down 50%" },
      { username: "WhaleWatcher", message: "Big whale just bought in 🐋" },
      { username: "DumpItPls", message: "When dump? Need to buy more" },
      { username: "CryptoNoob", message: "What's a blockchain?" },
      { username: "SatoshiStan", message: "Bitcoin is digital gold" },
      { username: "AltCoinAddict", message: "This altcoin season is insane" },
      
      // Anime/Gaming
      { username: "AnimeEnjoyer", message: "Lucy your takes are fire 🔥" },
      { username: "BlockchainBaka", message: "When Eva collab?" },
      { username: "TechOtaku", message: "AI waifus are the future" },
      { username: "GamingGuru", message: "Lucy can you beat me in Starcraft?" },
      { username: "WaifuHunter", message: "Lucy best girl confirmed" },
      { username: "MechaPilot", message: "Gundam Wing or Seed?" },
      { username: "JRPGFan", message: "Persona 6 when???" },
      { username: "RetroGamer", message: "Anyone still play arcade games?" },
      { username: "SpeedRunner", message: "Just got WR on Mario 64!" },
      { username: "VTuberFan", message: "Lucy should collab with Gura" },
      
      // Food/Lifestyle
      { username: "RamenLover", message: "Family mart chicken > everything" },
      { username: "FoodieLife", message: "Just had the best takoyaki ever" },
      { username: "CoffeeAddict", message: "4th espresso today, no regrets" },
      { username: "NightOwl", message: "3AM ramen hits different" },
      { username: "HealthNut", message: "Y'all need to eat some vegetables" },
      { username: "SushiMaster", message: "Fresh uni is worth the price" },
      { username: "PizzaTime", message: "Pineapple on pizza: thoughts?" },
      { username: "BubbleTeaQueen", message: "Taro or matcha?" },
      { username: "InstantNoodles", message: "Cooking is overrated tbh" },
      { username: "IceCreamDreams", message: "Mint chocolate chip supremacy" },
      
      // Tech/AI/Future
      { username: "CodeWizard", message: "Just deployed my first AI model" },
      { username: "TechBro", message: "Web3 will change everything" },
      { username: "AISkeptic", message: "When do robots take over?" },
      { username: "CyberpunkFan", message: "We're living in 2077 already" },
      { username: "QuantumLeap", message: "Quantum computing is wild" },
      { username: "RobotLover", message: "Boston Dynamics latest video 🤖" },
      { username: "MetaverseMike", message: "VR is the future of everything" },
      { username: "DataScientist", message: "Machine learning is just statistics" },
      { username: "DevOps", message: "It works on my machine 🤷" },
      { username: "OpenAIFan", message: "ChatGPT changed my life" },
      
      // Random/Chaos
      { username: "ChaoticNeutral", message: "This stream is pure chaos and I love it" },
      { username: "RandomGuy123", message: "What's the meaning of life?" },
      { username: "PhilosophyBro", message: "If a tree falls in the forest..." },
      { username: "MemeLord", message: "This is the way" },
      { username: "PunMaster", message: "I'm reading a book about anti-gravity. It's impossible to put down!" },
      { username: "DadJokes", message: "Why don't scientists trust atoms? Because they make up everything!" },
      { username: "Conspiracy", message: "Birds aren't real, change my mind" },
      { username: "TimeTravel", message: "Came here from 2030, you're doing great" },
      { username: "MatrixGlitch", message: "There is no spoon" },
      { username: "SimulationTheory", message: "We're all NPCs in Lucy's world" },
      
      // Music/Entertainment
      { username: "MusicLover", message: "What's your favorite J-pop group?" },
      { username: "KPopStan", message: "BTS vs Blackpink?" },
      { username: "VocaloidFan", message: "Hatsune Miku concert was amazing" },
      { username: "MetalHead", message: "Babymetal is actually good" },
      { username: "EDMVibes", message: "This stream needs more bass drops" },
      { username: "ClassicalNerd", message: "Bach > modern music fight me" },
      { username: "IndieHipster", message: "You probably haven't heard of my favorite band" },
      { username: "SoundCloudRapper", message: "Check out my mixtape 🔥" },
      { username: "VinylCollector", message: "Digital music has no soul" },
      { username: "ConcertGoer", message: "Live music hits different" },
      
      // Internet Culture
      { username: "RedditUser", message: "Thanks for the gold, kind stranger!" },
      { username: "TwitterMain", message: "This would be a great tweet" },
      { username: "TikTokDancer", message: "Someone needs to make a dance to this" },
      { username: "YouTuber", message: "Don't forget to smash that like button" },
      { username: "DiscordMod", message: "This chat needs more moderation" },
      { username: "TwitchStreamer", message: "POG in the chat" },
      { username: "InstagramInfluencer", message: "This aesthetic is immaculate" },
      { username: "LinkedInLunatic", message: "Agree? Thoughts? 🤔" },
      { username: "FacebookBoomer", message: "Back in my day..." },
      { username: "SnapchatGhost", message: "This deserves a story post" },
      
      // Weather/Daily Life
      { username: "WeatherWatcher", message: "It's raining here, perfect streaming weather" },
      { username: "MondayMood", message: "Monday blues hitting hard" },
      { username: "WeekendWarrior", message: "Friday night vibes!" },
      { username: "MorningPerson", message: "Good morning from Tokyo!" },
      { username: "NightShift", message: "Just got off work, perfect timing" },
      { username: "StudentLife", message: "Procrastinating on my thesis rn" },
      { username: "WorkFromHome", message: "Pajamas and productivity" },
      { username: "CommutterHell", message: "Stuck in traffic, watching on mobile" },
      { username: "RetiredGrandpa", message: "Young people and their internets" },
      { username: "NewParent", message: "Baby finally asleep, time for Lucy" },
      
      // Sports/Fitness
      { username: "GymBro", message: "Just hit a new PR! 💪" },
      { username: "RunnerHigh", message: "10K done, endorphins hitting" },
      { username: "CouchPotato", message: "Exercise is watching sports, right?" },
      { username: "YogaMaster", message: "Namaste and good vibes" },
      { username: "SoccerFan", message: "Messi or Ronaldo?" },
      { username: "BasketballNerd", message: "NBA playoffs are intense" },
      { username: "EsportsAthlete", message: "Gaming is a sport too" },
      { username: "OlympicDreamer", message: "Paris 2024 was amazing" },
      { username: "MartialArtist", message: "Discipline over motivation" },
      { username: "SkateOrDie", message: "Just landed my first kickflip" },
      
      // Fashion/Style
      { username: "Fashionista", message: "That outfit is chef's kiss 👌" },
      { username: "ThriftFinds", message: "Vintage > brand new" },
      { username: "SneakerHead", message: "Those kicks are fire" },
      { username: "MinimalistStyle", message: "Less is more" },
      { username: "HarajukuVibes", message: "Love the colorful aesthetic" },
      { username: "GothGirl", message: "Black everything forever" },
      { username: "StreetWear", message: "Hypebeast culture is wild" },
      { username: "CosplayQueen", message: "Next convention outfit ideas?" },
      
      // Spam/Trolls (for Lucy to react to)
      { username: "SpamBot123", message: "Check out my project [link]" },
      { username: "ScammerAlert", message: "SEND ME 1 ETH GET 2 BACK!!!" },
      { username: "FakeGiveaway", message: "FREE MONEY CLICK HERE NOW" },
      { username: "CopyPasta", message: "Navy Seal copypasta but make it anime" },
      { username: "TrollKing", message: "Lucy is mid tbh" },
      { username: "AttentionSeeker", message: "NOTICE ME SENPAI!!!" },
      { username: "OffTopic", message: "Anyone want to buy my car?" },
      { username: "WrongStream", message: "This isn't Fortnite gameplay" },
      { username: "Hater", message: "VTubers are cringe" },
      { username: "DebbieDowner", message: "Everything is going downhill" }
    ];
    
    const randomMessage = sampleMessages[Math.floor(Math.random() * sampleMessages.length)];
    addChatMessage({
      ...randomMessage,
      timestamp: Date.now()
    });
    
    // Simulate viewer count changes
    setViewerCount(Math.floor(Math.random() * 500) + 100);
    updateTimestamp();
  };
  
  return { simulateIncomingMessage };
};